import type { GetTransactionCodeResponse } from "@/openapi/generated";

export function toTransactionCodeNumericId(
  id: GetTransactionCodeResponse["id"],
): number {
  return typeof id === "number" ? id : Number(id);
}

function toSortOrderNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

/** Stable catalog / picker order: `sortOrder`, then code. */
export function sortTransactionCodesBySortOrder(
  items: GetTransactionCodeResponse[],
): GetTransactionCodeResponse[] {
  return [...items].sort((a, b) => {
    const orderDiff =
      toSortOrderNumber(a.sortOrder) - toSortOrderNumber(b.sortOrder);
    if (orderDiff !== 0) return orderDiff;
    return a.code.localeCompare(b.code);
  });
}

export function applyTransactionCodeOrder(
  items: GetTransactionCodeResponse[],
  orderedIds: Array<number | string>,
): GetTransactionCodeResponse[] {
  const byId = new Map(
    items.map((item) => [toTransactionCodeNumericId(item.id), item]),
  );
  const seen = new Set<number>();
  const next: GetTransactionCodeResponse[] = [];

  for (const [index, rawId] of orderedIds.entries()) {
    const id = typeof rawId === "number" ? rawId : Number(rawId);
    if (!Number.isFinite(id) || seen.has(id)) continue;
    const item = byId.get(id);
    if (!item) continue;
    seen.add(id);
    next.push({ ...item, sortOrder: index });
  }

  for (const item of items) {
    const id = toTransactionCodeNumericId(item.id);
    if (seen.has(id)) continue;
    next.push(item);
  }

  return next;
}

export function getTransactionCodeOrderedIds(
  items: GetTransactionCodeResponse[],
): number[] {
  return items.map((item) => toTransactionCodeNumericId(item.id));
}

export function sameTransactionCodeOrder(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
